import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRouter, useSearch } from "@tanstack/react-router";
import { Route } from "@/routes/__root";
import { V2Loader, cn } from "web-design-system";
import {
  PurchaseTransactionOrder,
  StpTransactionOrder,
  SwitchTransactionOrder,
  SwpTransactionOrder,
  TransactionOrder,
  XSIPTransactionOrder,
} from "creso-core/types";
import {
  useClient,
  useDraftTransactionOrders,
  useSchemes,
} from "creso-core/hooks";
import { TransactionOrderType } from "creso-core/constants";
import TransactionForm from "./transaction-form";
import TransactionOrdersCart from "./orders-cart";

interface TransactionContext {
  selectedOrder?: TransactionOrder | null;
  investment_account_guid?: string;
  collectionId?: number;
  collectionType?: string;
  collectionTarget?: TransactionOrderType[];
  updateContext?: (
    value: Partial<Omit<TransactionContext, "updateContext">>
  ) => void;
}

type TransactionContextUpdateValue = Partial<
  Omit<TransactionContext, "updateContext">
>;

export const TransactionContext = createContext<TransactionContext>({});

export default function TransactionWizard() {
  const {
    client_id = 0,
    type,
    isin,
    action,
    folio_id,
    collection_id,
    collection_type,
    collection_target,
  } = useSearch({
    from: Route.id,
  });

  const [contextData, setContextData] = useState<TransactionContextUpdateValue>(
    {
      collectionId: collection_id,
      collectionType: collection_type,
      collectionTarget: collection_target,
    }
  );

  const { schemes, isFetching: isFetchingScheme } = useSchemes({
    isin,
    type: type === TransactionOrderType.XSIP ? "sip" : "all",
    enabled: !!isin,
  });

  useEffect(() => {
    /**
     * selectedOrder should be only only when there is an isin passed in the url
     * or when an draft order is manually selected from cart.
     * If you are updating this isin check in the future, make sure to fix the
     * import fund from proposals feature since it relies on selectedOrder being empty.
     */
    if (isFetchingScheme || !isin) return;
    if (
      [TransactionOrderType.STP, TransactionOrderType.SWITCH].includes(
        type as TransactionOrderType
      )
    ) {
      setContextData((prev) => ({
        ...prev,
        selectedOrder: {
          switch_out_scheme_isin: isin,
          switch_out_scheme_id: schemes?.[0]?.id,
          folio_id,
          type,
        } as StpTransactionOrder | SwitchTransactionOrder,
      }));
    } else {
      setContextData((prev) => ({
        ...prev,
        selectedOrder: {
          scheme_isin: isin,
          scheme_id: schemes?.[0]?.id,
          type,
          folio_id,
        } as
          | PurchaseTransactionOrder
          | XSIPTransactionOrder
          | SwpTransactionOrder,
      }));
    }
  }, [type, isin, schemes, isFetchingScheme, folio_id]);

  const { data: client, isLoading } = useClient(client_id);

  const { data: draftOrders } = useDraftTransactionOrders(
    client?.investment_account ?? "",
    !!client?.investment_account && action === "edit"
  );

  /**
   * This effect needs to run when the dialog is opened from draft orders in home page.
   * Selecting an order also triggers this effect but we don't want to update the selected order in that case.
   */
  useEffect(() => {
    if (action === "edit" && draftOrders?.orders?.results?.length) {
      setContextData((prev) => ({
        ...prev,
        selectedOrder: prev.selectedOrder?.id
          ? prev.selectedOrder
          : draftOrders.orders.results[0],
      }));
    }
  }, [action, draftOrders]);

  const router = useRouter();

  const updateContext = useCallback(
    (value: TransactionContextUpdateValue) => {
      setContextData((prev) => ({
        ...prev,
        ...value,
      }));
      if (value.selectedOrder) {
        router.navigate({
          to: ".",
          search: (prev) => ({ ...prev, action: "edit" }),
          replace: true,
        });
      } else {
        router.navigate({
          to: ".",
          search: (prev) => ({ ...prev, action: "create" }),
          replace: true,
        });
      }
    },
    [router]
  );

  const contextValue = useMemo(
    () => ({
      ...contextData,
      updateContext,
    }),
    [contextData, updateContext]
  );

  useEffect(() => {
    setContextData((prev) => ({
      ...prev,
      investment_account_guid: client?.investment_account ?? "",
      collectionId: collection_id,
      collectionType: collection_type,
      collectionTarget: collection_target,
    }));
  }, [client, collection_id, collection_type, collection_target]);

  useEffect(() => {
    return () => {
      setContextData({});
    };
  }, []);

  return (
    <TransactionContext.Provider value={contextValue}>
      <div
        className={cn(
          "flex flex-col rounded-lg overflow-hidden",
          action === "view" ? "w-100" : "w-260"
        )}
      >
        <V2Loader
          isLoading={
            isLoading ||
            !contextData.investment_account_guid ||
            isFetchingScheme
          }
          className={cn("overflow-hidden")}
        >
          <div className="flex flex-row flex-1 size-full overflow-hidden">
            <TransactionOrdersCart />
            <TransactionForm />
          </div>
        </V2Loader>
      </div>
    </TransactionContext.Provider>
  );
}
